<template>
  <v-container v-if="$store.getters.loadingApp !== 1">
    <h2
      style="width: 100%"
      class="ppc_darkgreen--text my-5"
    >
      Images for Services
    </h2>

    <v-card
      flat
      class="d-flex flex-row flex-wrap my-5"
    >
      <v-file-input
        v-model="selectedImageFile"
        :rules="fileRules"
        small-chips
        show-size
        label="Select JPG (exactly 300 × 200 px)"
        accept="image/jpeg,.jpg,.jpeg"
        @change="selectFile"
      />
    </v-card>

    <div class="d-flex flex-row flex-wrap mb-7">
      <v-card
        v-for="(image, imageIndex) in $store.state.available_images"
        :key="imageIndex"
        flat
      >
        <v-img
          :src="getImage(image.id)"
          class="white--text align-end"
          gradient="to bottom, rgba(0,0,0,.1), rgba(0,0,0,.5)"
          max-height="200px"
          max-width="300px"
        >
          <v-card-title class="white--text">
            {{ image.name }} 
            <v-btn 
              fab 
              color="ppc_pink" 
              x-small 
              absolute
              left
              top
              class="mt-7"
              @click="downloadImage(image.id)"
            >
              <v-icon color="white">
                mdi-download
              </v-icon>
            </v-btn>
            <v-btn 
              fab 
              color="ppc_pink" 
              x-small 
              absolute
              left
              top
              class="mt-7 ml-9"
              @click="deleteImage(image.id)"
            >
              <v-icon color="white">
                $vuetify.icons.icon_mdi_delete
              </v-icon>
            </v-btn>
          </v-card-title>
        </v-img>
      </v-card>
    </div>
    <v-divider />
    <div class="d-flex flex-column mb-7 align-end">
      <v-btn
        color="ppc_darkgreen darken-1"
        text
        @click="close"
      >
        Close
      </v-btn>
    </div>

    <v-snackbar
      v-model="alertUploading"
      :timeout="timeout"
      top
      color="success"
    >
      <v-progress-linear
        v-model="progressAdvance"
        color="ppc_grey"
        height="15"
        reactive
      />
    </v-snackbar>
  </v-container>
</template>

<script>

export default {
    data() {
      return {
        serviceNr: null,
        currentFile: null,
        currentFileName: null,
        selectedImageFile: null,
        alertUploading: false,
        timeout: 3000,
        fileRules: [
          file => !file || file.size < 2e6 ||
            'File size should be less than 2 MB!',
        ],
        imageUrlCache: {},
      };
    },

    computed: {
        progressAdvance () {
            return this.$store.getters.progressAdvance
        },
    },

    beforeCreate () {
        this.$store.dispatch('setLoadingApp', 1)
    },

    created () {
      this.initialize()
    },

    methods: {

        async initialize() {
            await this.$store.dispatch('getAvailableImages');
            await this.$store.dispatch('getAvailableServices');
            this.$store.dispatch('setLoadingApp', 0);
        },

        async selectFile(file) {
          const selectedFile = Array.isArray(file) ? file[0] : file;

          if (!selectedFile) {
            this.currentFile = null;
            return;
          }

          this.currentFile = selectedFile;

          // Reject non-JPEG files.
          if (
            selectedFile.type !== 'image/jpeg' &&
            !/\.(jpe?g)$/i.test(selectedFile.name)
          ) {
            this.$store.dispatch('errorMessage', 'Please select a JPG/JPEG image.');
            this.selectedImageFile = null;
            this.currentFile = null;
            return;
          }

          let objectUrl;

          try {
            const dimensions = await new Promise((resolve, reject) => {
              const img = new Image();
              objectUrl = URL.createObjectURL(selectedFile);

              img.onload = () => resolve({
                width: img.naturalWidth,
                height: img.naturalHeight,
              });

              img.onerror = () => reject(new Error('Cannot read image dimensions'));

              img.src = objectUrl;
            });

            console.log('Selected image dimensions:', dimensions);

            if (dimensions.width !== 300 || dimensions.height !== 200) {
              this.$store.dispatch(
                'errorMessage',
                `Image must be exactly 300 × 200 px. Selected image is ${dimensions.width} × ${dimensions.height} px.`
              );

              this.selectedImageFile = null;
              this.currentFile = null;
              this.currentFileName = null;
              return; // Critical: do not upload.
            }

            // Only valid images reach this line.
            await this.uploadImage();
          } catch (error) {
            console.error('Image validation failed:', error);
            this.$store.dispatch('errorMessage', 'Could not validate the image.');
            this.selectedImageFile = null;
            this.currentFile = null;
          } finally {
            if (objectUrl) URL.revokeObjectURL(objectUrl);
          }
        },

        async uploadImage() {
          const file = this.currentFile;

          if (!file) {
            this.$store.dispatch('successMessage', 'Please select a file');
            return;
          }

          this.alertUploading = true;

          try {
            // Convert the selected file to a Data URL.
            const dataUrl = await new Promise((resolve, reject) => {
              const reader = new FileReader();

              reader.onload = () => resolve(reader.result);
              reader.onerror = () => reject(
                reader.error || new Error('Could not read image')
              );

              reader.readAsDataURL(file);
            });

            if (typeof dataUrl !== 'string' || !dataUrl.includes(',')) {
              throw new Error('Invalid image data');
            }

            // Build the payload here; do not expect it as a parameter.
            const payload = {
              name: file.name,
              image: dataUrl.split(',')[1],
              mime_type: file.type || 'image/jpeg',
              encoding: 'base64',
            };

            await this.$store.dispatch('uploadImage', payload);

            // Refresh the gallery after the upload succeeds.
            await this.$store.dispatch('getAvailableImages');

            this.selectedImageFile = null;
            this.currentFile = null;
            this.currentFileName = null;

            this.$store.dispatch(
              'successMessage',
              'Image uploaded successfully'
            );
          } catch (error) {
            console.error('Image upload failed:', error);
            this.$store.dispatch('errorMessage', 'Image upload failed');
          } finally {
            this.alertUploading = false;
          }
        },

        async deleteImage(payload) {

          const availableServices = await this.$store.dispatch(
            'getAvailableServicesEdit'
          );

          const tmp = availableServices.filter(function(select_item) {
              return select_item.image === payload;
              }
          )

          console.log('tmp', tmp, this.$store.state.available_services);

          if (tmp.length > 0) {
              this.$store.dispatch('errorMessage', 'Cannot delete image. Being used by ' + tmp.length + ' services like ' + tmp[0].name);
          } else {
              this.$store.dispatch('deleteImage', payload);
          }

        },

        downloadImage(id) {
          try {
            const image = this.$store.state.available_images.find(
              item => item.id === id
            );

            if (!image || !image.image) {
              throw new Error(`Image ${id} not found in store`);
            }

            let bytes;

            if (image.image.type === 'Buffer' && Array.isArray(image.image.data)) {
              bytes = new Uint8Array(image.image.data);
            } else if (typeof image.image === 'string') {
              // Use this branch if the image is stored as Base64 in the response.
              const binary = atob(image.image);
              bytes = new Uint8Array(binary.length);

              for (let i = 0; i < binary.length; i++) {
                bytes[i] = binary.charCodeAt(i);
              }
            } else {
              throw new Error('Unsupported image data format');
            }

            const blob = new Blob([bytes], { type: 'image/jpeg' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');

            link.href = url;
            link.download = image.name || `image-${id}.jpg`;

            document.body.appendChild(link);
            link.click();
            link.remove();

            // Revoke after the browser has had time to start the download.
            setTimeout(() => URL.revokeObjectURL(url), 1000);
          } catch (error) {
            console.error('Image download failed:', error);
            this.$store.dispatch('errorMessage', 'Image download failed');
          }
        },

        getImage(id) {
          const image = this.$store.state.available_images?.find(
            item => item.id === id
          );

          if (!image || !image.image) return null;

          // Reuse the same URL on subsequent renders.
          if (this.imageUrlCache[id]) {
            return this.imageUrlCache[id];
          }

          let bytes;

          if (image.image.type === 'Buffer' && Array.isArray(image.image.data)) {
            bytes = new Uint8Array(image.image.data);
          } else if (typeof image.image === 'string') {
            const binary = atob(image.image);
            bytes = new Uint8Array(binary.length);

            for (let i = 0; i < binary.length; i++) {
              bytes[i] = binary.charCodeAt(i);
            }
          } else {
            return null;
          }

          const url = URL.createObjectURL(
            new Blob([bytes], { type: 'image/jpeg' })
          );

          this.$set(this.imageUrlCache, id, url);

          return url;
        },

        close() {
            this.$router.push({name: 'AvailableServices'})
        },

    },
}
</script>

<style>
    .v-card__title {
        color: #35b729;
        word-break: normal !important;
    }
    .v-card__subtitle { padding-top: 0; padding-bottom: 0;}
    .pos_absolute {
        position: absolute;
    }
</style>